import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuCalendar,
  LuChevronLeft,
  LuChevronRight,
  LuCircleCheck,
  LuClock3,
  LuEye,
  LuFileText,
  LuHourglass,
  LuPackage,
  LuSearch,
  LuTruck,
} from "react-icons/lu";
import {
  MY_DISPUTE_CATEGORY_OPTIONS,
  MY_DISPUTE_PRIORITY_OPTIONS,
  MY_DISPUTE_STATUS_OPTIONS,
  fetchMyDisputeStats,
  fetchMyDisputes,
  myDisputeExtractMessage,
} from "../../services/myDispute.service";
import "../../assets/styles/my_disputes.scss";

const PAGE_OPTIONS = [10, 20, 50];

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatStatus(status) {
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatReferenceType(type) {
  const value = String(type || "").trim();
  if (!value) return "";
  const map = {
    rfq: "Request for Quotation (RFQ)",
    "request for quotation (rfq)": "Request for Quotation (RFQ)",
    invoice: "Invoice",
    po: "Purchase Order (PO)",
    "purchase order": "Purchase Order (PO)",
    order: "Order",
    shipment: "Shipment",
  };
  return map[value.toLowerCase()] || value;
}

function truncateText(value, max = 72) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function statusClass(status) {
  const normalized = String(status || "").toLowerCase();
  return `md-pill md-status md-status--${normalized.replace(/\s+/g, "_")}`;
}

function priorityClass(priority) {
  const normalized = String(priority || "").toLowerCase();
  if (normalized === "urgent") return "md-pill md-priority md-priority--urgent";
  if (normalized === "high") return "md-pill md-priority md-priority--high";
  if (normalized === "medium") return "md-pill md-priority md-priority--medium";
  return "md-pill md-priority md-priority--low";
}

function categoryIcon(category) {
  const value = String(category || "").toLowerCase();
  if (value.includes("deliver") || value.includes("shipment") || value.includes("not delivered")) {
    return LuTruck;
  }
  if (value.includes("quality") || value.includes("damage") || value.includes("defective")) {
    return LuPackage;
  }
  if (value.includes("payment") || value.includes("refund")) {
    return LuFileText;
  }
  return LuFileText;
}

function buildPageNumbers(currentPage, totalPages, maxVisible = 5) {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export default function MyDisputesPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
  });

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchMyDisputeStats();
      setStats({
        total: Math.trunc(Number(data.total) || 0),
        inProgress: Math.trunc(Number(data.inProgress) || 0),
        resolved: Math.trunc(Number(data.resolved) || 0),
        closed: Math.trunc(Number(data.closed) || 0),
      });
    } catch {
      // Keep prior stats.
    }
  }, []);

  const loadDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMyDisputes({
        search: query.trim() || undefined,
        status: status || undefined,
        category: category || undefined,
        priority: priority || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        pageSize,
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Math.trunc(Number(data.total) || 0));
    } catch (error) {
      toast.error(myDisputeExtractMessage(error, "Failed to load your disputes."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query, status, category, priority, dateFrom, dateTo, page, pageSize]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const timer = setTimeout(loadDisputes, 250);
    return () => clearTimeout(timer);
  }, [loadDisputes]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageNumbers = useMemo(
    () => buildPageNumbers(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const resetFilters = () => {
    setQuery("");
    setStatus("");
    setCategory("");
    setPriority("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const openDetail = (dispute) => {
    navigate(`/dashboard/help-center/my-disputes/${dispute.id}`);
  };

  return (
    <div className="md-page">
      <header className="md-head">
        <div>
          <nav className="md-breadcrumb" aria-label="Breadcrumb">
            <Link to="/home">Home</Link>
            <span aria-hidden>&gt;</span>
            <Link to="/help-center">Help Center</Link>
            <span aria-hidden>&gt;</span>
            <span aria-current="page">My Disputes</span>
          </nav>
          <h1>My Disputes</h1>
          <p>Track and manage all your submitted disputes in one place.</p>
        </div>
        <div className="md-head__actions">
          <button
            type="button"
            className="md-btn md-btn--primary"
            onClick={() => navigate("/submit-dispute")}
          >
            Submit Dispute
          </button>
        </div>
      </header>

      <section className="md-stats">
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--blue">
            <LuFileText aria-hidden />
          </span>
          <div>
            <strong>{stats.total.toLocaleString()}</strong>
            <span>Total Disputes</span>
            <small>All time</small>
          </div>
        </article>
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--orange">
            <LuHourglass aria-hidden />
          </span>
          <div>
            <strong>{stats.inProgress.toLocaleString()}</strong>
            <span>In Progress</span>
            <small>Active disputes</small>
          </div>
        </article>
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--green">
            <LuCircleCheck aria-hidden />
          </span>
          <div>
            <strong>{stats.resolved.toLocaleString()}</strong>
            <span>Resolved</span>
            <small>Successfully closed</small>
          </div>
        </article>
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--purple">
            <LuClock3 aria-hidden />
          </span>
          <div>
            <strong>{stats.closed.toLocaleString()}</strong>
            <span>Closed</span>
            <small>No further action</small>
          </div>
        </article>
      </section>

      <div className="md-toolbar">
        <label className="md-search">
          <LuSearch aria-hidden />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search by Dispute ID, Order Ref, Seller..."
          />
        </label>
        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        >
          {MY_DISPUTE_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || "all-status"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={category}
          onChange={(event) => {
            setCategory(event.target.value);
            setPage(1);
          }}
        >
          {MY_DISPUTE_CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value || "all-category"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(event) => {
            setPriority(event.target.value);
            setPage(1);
          }}
        >
          {MY_DISPUTE_PRIORITY_OPTIONS.map((opt) => (
            <option key={opt.value || "all-priority"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <label className="md-date">
          <LuCalendar aria-hidden />
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setPage(1);
            }}
            aria-label="From date"
          />
        </label>
        <label className="md-date">
          <LuCalendar aria-hidden />
          <input
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setPage(1);
            }}
            aria-label="To date"
          />
        </label>
        <button type="button" className="md-btn md-btn--ghost" onClick={resetFilters}>
          Reset
        </button>
      </div>

      <section className="md-card">
        <div className="md-table-wrap">
          <table className="md-table">
            <thead>
              <tr>
                <th>Dispute ID</th>
                <th>Order Reference</th>
                <th>Category</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Submitted On</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="md-empty">
                    Loading disputes...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="md-empty">
                    No disputes found.{" "}
                    <button type="button" className="md-link-btn" onClick={() => navigate("/submit-dispute")}>
                      Submit a dispute
                    </button>
                  </td>
                </tr>
              ) : (
                items.map((dispute) => {
                  const CategoryIcon = categoryIcon(dispute.category);
                  return (
                    <tr key={dispute.id}>
                      <td>
                        <div className="md-id-cell">
                          <button
                            type="button"
                            className="md-id-link"
                            onClick={() => openDetail(dispute)}
                          >
                            {dispute.disputeNumber}
                          </button>
                          <span className="md-id-chip">#{String(dispute.id).padStart(6, "0")}</span>
                        </div>
                      </td>
                      <td>
                        <div className="md-stack">
                          <strong title={dispute.referenceNumber || undefined}>
                            {truncateText(dispute.referenceNumber || "—", 22)}
                          </strong>
                          <span title={dispute.referenceType || undefined}>
                            {formatReferenceType(dispute.referenceType) || "—"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="md-category">
                          <span className="md-category__icon" aria-hidden>
                            <CategoryIcon />
                          </span>
                          <span title={dispute.category}>{truncateText(dispute.category, 28)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="md-stack">
                          <strong title={dispute.subject}>{truncateText(dispute.subject, 42)}</strong>
                          <span title={dispute.description}>
                            {truncateText(dispute.description, 54)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={statusClass(dispute.status)}>
                          {formatStatus(dispute.status)}
                        </span>
                      </td>
                      <td>
                        <span className={priorityClass(dispute.priority)}>{dispute.priority}</span>
                      </td>
                      <td>
                        <div className="md-stack">
                          <strong>{formatDate(dispute.createdAt)}</strong>
                          <span>{formatTime(dispute.createdAt)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="md-stack">
                          <strong>{formatDate(dispute.updatedAt)}</strong>
                          <span>{formatTime(dispute.updatedAt)}</span>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="md-icon-btn"
                          title="View details"
                          onClick={() => openDetail(dispute)}
                        >
                          <LuEye aria-hidden />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <footer className="md-footer">
          <span>
            Showing {items.length ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, total)} of {total} disputes
          </span>
          <div className="md-pagination">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              <LuChevronLeft aria-hidden />
            </button>
            {pageNumbers.map((item) => (
              <button
                key={item}
                type="button"
                className={currentPage === item ? "is-active" : ""}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            >
              <LuChevronRight aria-hidden />
            </button>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </footer>
      </section>

      <aside className="md-help-banner">
        <div>
          <strong>How dispute resolution works?</strong>
          <p>Learn about our dispute process, timelines, and what to expect after submission.</p>
        </div>
        <Link to="/help-center" className="md-btn md-btn--ghost">
          Learn More
        </Link>
      </aside>
    </div>
  );
}
